import React from 'react';
import {
  Avatar, Box, CircularProgress, Divider,
  IconButton, Stack, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Assignment, AccessTime, Close, LocationOn,
  OpenInNew, Person, Phone, Timeline,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ACTIVITY_STAGES, NET_QUALITY_CONFIG, STATUS_CONFIG } from '../constants';
import { getStatus, flatFormData, extractImages, prettyKey } from '../utils';
import ActivityStageBlock from './ActivityStageBlock';
import PhotoCard from './PhotoCard';
import EntryModal from './EntryModal';

/**
 * Slide-in side panel rendered on top of the map when a user is selected.
 * Displays two tabs: Activity timeline and a unified Trail/Tasks timeline.
 *
 * Props:
 *   row            – active activity row
 *   trail          – array of location ping objects
 *   trailLoading   – boolean
 *   onClose        – close handler
 *   visible        – drives the slide-in/out animation
 *   tasks          – array of task entry objects
 *   tasksLoading   – boolean
 *   selectedEntry  – currently-expanded task entry (for EntryModal)
 *   entryModalOpen – boolean
 *   onOpenEntry    – (entry) => void
 *   onCloseEntry   – () => void
 */
export default function MapSidePanel({
  row, trail, trailLoading, onClose, visible,
  tasks, tasksLoading,
  selectedEntry, entryModalOpen, onOpenEntry, onCloseEntry,
}) {
  const [tab, setTab] = React.useState(0);

  // Reset to Activity tab whenever the selected user changes
  React.useEffect(() => { setTab(0); }, [row?._id]);

  if (!row) return null;

  const { author, project, region } = row;
  const status    = getStatus(row);
  const statusCfg = STATUS_CONFIG[status];
  const activity  = row.currentLocation || row.checkin;

  return (
    <Box sx={{
      position: 'absolute', top: 0, left: 0, bottom: 0,
      width: 360,
      background: '#ffffff',
      borderRight: '1px solid rgba(16,117,186,0.15)',
      borderRadius: '16px 0 0 16px',
      zIndex: 1001,
      display: 'flex', flexDirection: 'column',
      boxShadow: '6px 0 32px rgba(15,33,55,0.13)',
      animation: visible ? 'slideInLeft 0.22s ease-out' : 'slideOutLeft 0.2s ease-in forwards',
      overflow: 'hidden',
    }}>

      {/* ── Header ── */}
      <Box sx={{
        flexShrink: 0, position: 'relative',
        background: `linear-gradient(135deg, ${alpha(statusCfg.color, 0.1)} 0%, ${alpha('#1075BA', 0.05)} 100%)`,
        borderBottom: `1px solid ${alpha(statusCfg.color, 0.12)}`,
        p: 2, pb: 1.75,
      }}>
        <IconButton
          size="small" onClick={onClose}
          sx={{ position: 'absolute', top: 10, right: 10, color: '#4a6a85', '&:hover': { background: 'rgba(15,33,55,0.07)', color: '#ef4444' } }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 4 }}>
          {/* Avatar */}
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Avatar
              src={author?.photoURL}
              sx={{
                width: 52, height: 52, border: `2.5px solid ${statusCfg.color}`,
                boxShadow: `0 0 0 3px ${alpha(statusCfg.color, 0.2)}`,
                fontSize: '1.1rem', fontWeight: 700,
              }}
            >
              {author?.firstName?.[0]}
            </Avatar>
            <Box sx={{
              position: 'absolute', bottom: 1, right: 1,
              width: 12, height: 12, borderRadius: '50%',
              background: statusCfg.color, border: '2px solid #fff',
              boxShadow: `0 0 6px ${alpha(statusCfg.color, 0.6)}`,
            }} />
          </Box>

          {/* Name + badges */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f2137', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {author?.firstName} {author?.lastName}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#4a6a85', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              @{author?.username} · {author?.role?.title || '—'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
              {[
                { text: statusCfg.label, color: statusCfg.color, bg: statusCfg.bg, border: statusCfg.border },
                project?.title && { text: project.title, color: '#1075BA', bg: '#e6f2ff', border: '#99ccf5' },
                (region?.title || author?.region?.title) && { text: region?.title || author?.region?.title, color: '#4a6a85', bg: '#f0f4f7', border: '#c8d4dc' },
              ].filter(Boolean).map((chip) => (
                <Box key={chip.text} sx={{
                  fontSize: '0.65rem', fontWeight: 700, color: chip.color,
                  background: chip.bg, border: `1px solid ${chip.border}`,
                  px: 0.75, py: 0.15, borderRadius: '5px',
                }}>
                  {chip.text}
                </Box>
              ))}
            </Box>
          </Box>
        </Stack>

        {/* Contact + coords */}
        <Stack spacing={0.4} sx={{ mt: 1.25 }}>
          {author?.mobileNumber && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Phone sx={{ fontSize: 12, color: '#4a6a85' }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#0f2137' }}>{author.mobileNumber}</Typography>
            </Box>
          )}
          {activity && activity !== 'NA' && activity.lat && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <LocationOn sx={{ fontSize: 12, color: '#4a6a85' }} />
              <Typography sx={{ fontSize: '0.72rem', color: '#4a6a85', fontFamily: 'monospace' }}>
                {parseFloat(activity.lat).toFixed(5)}, {parseFloat(activity.long).toFixed(5)}
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>

      {/* ── Tab bar ── */}
      <Box sx={{ flexShrink: 0, display: 'flex', borderBottom: '1px solid rgba(16,117,186,0.1)' }}>
        {[
          { icon: <Person sx={{ fontSize: 14 }} />, label: 'Activity' },
          {
            icon: <Timeline sx={{ fontSize: 14 }} />,
            label: (trailLoading || tasksLoading)
              ? 'Trail…'
              : `Trail${(trail.length + tasks.length) ? ` · ${trail.length + tasks.length}` : ''}`,
            loading: trailLoading || tasksLoading,
          },
        ].map((t, i) => (
          <Box key={i} onClick={() => setTab(i)} sx={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
            py: 1.1, cursor: 'pointer', fontSize: '0.72rem',
            fontWeight: tab === i ? 700 : 500,
            color: tab === i ? '#1075BA' : '#6b7f8e',
            borderBottom: tab === i ? '2.5px solid #1075BA' : '2.5px solid transparent',
            transition: 'all 0.15s', userSelect: 'none',
            '&:hover': { background: 'rgba(16,117,186,0.04)' },
          }}>
            {t.icon}
            <span style={{ marginLeft: 4 }}>{t.label}</span>
            {t.loading && <CircularProgress size={9} sx={{ color: '#1075BA', ml: 0.5 }} />}
          </Box>
        ))}
      </Box>

      {/* ── Tab 0: Activity ── */}
      {tab === 0 && (
        <ActivityTab row={row} activity={activity} />
      )}

      {/* ── Tab 1: Unified Trail + Tasks ── */}
      {tab === 1 && (
        <TrailTab
          row={row}
          trail={trail}
          tasks={tasks}
          trailLoading={trailLoading}
          tasksLoading={tasksLoading}
          onOpenEntry={onOpenEntry}
        />
      )}

      <EntryModal entry={selectedEntry} open={entryModalOpen} onClose={onCloseEntry} />
    </Box>
  );
}

// ── Activity sub-tab ──────────────────────────────────────────────────────────

function ActivityTab({ row, activity }) {
  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 1.75 }}>
      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1075BA', mb: 1.5 }}>
        Timeline & Photos
      </Typography>
      <Stack spacing={1.25}>
        {ACTIVITY_STAGES.map((stage) => (
          <ActivityStageBlock
            key={stage.key}
            stageKey={stage.key}
            label={stage.label}
            color={stage.color}
            data={row[stage.key]}
          />
        ))}
      </Stack>

      {activity && activity !== 'NA' && activity.lat && (
        <>
          <Divider sx={{ my: 2, borderColor: 'rgba(16,117,186,0.1)' }} />
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1075BA', mb: 1.25 }}>
            Last Known Location
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {[
              { l: 'Latitude',  v: activity.lat },
              { l: 'Longitude', v: activity.long },
              { l: 'Reported',  v: activity.timeReported ? format(new Date(activity.timeReported), "MMM d 'at' hh:mm a") : '—' },
              ...(activity.notes ? [{ l: 'Notes', v: activity.notes }] : []),
            ].map(({ l, v }) => (
              <Box key={l} sx={{
                background: '#f5f8fc', borderRadius: '10px', px: 1.5, py: 1,
                border: '1px solid rgba(16,117,186,0.1)',
                flex: '1 1 calc(50% - 4px)', minWidth: 'calc(50% - 4px)',
              }}>
                <Typography sx={{ fontSize: '0.63rem', color: '#4a6a85', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.2 }}>
                  {l}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#0f2137', fontFamily: (l === 'Latitude' || l === 'Longitude') ? 'monospace' : 'inherit' }}>
                  {v}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

// ── Trail + Tasks sub-tab ─────────────────────────────────────────────────────

const ACTIVITY_EVENTS = [
  { key: 'checkin',    label: 'Check In',    color: '#0a9e74', isBreakBoundary: false },
  { key: 'startbreak', label: 'Break',       color: '#7b1fa2', isBreakBoundary: true  },
  { key: 'endbreak',   label: 'Break End',   color: '#1075BA', isBreakBoundary: true  },
  { key: 'checkout',   label: 'Check Out',   color: '#c06000', isBreakBoundary: false },
];

function TrailTab({ row, trail, tasks, trailLoading, tasksLoading, onOpenEntry }) {
  // Build a merged, time-sorted list of events, pings, and tasks
  const items = React.useMemo(() => {
    const list = [];

    ACTIVITY_EVENTS.forEach(({ key, label, color, isBreakBoundary }) => {
      const data = row[key];
      if (!data || data === 'NA') return;
      const ts = data.timeReported ? new Date(data.timeReported) : null;
      list.push({ type: 'event', key, label, color, isBreakBoundary, data, ts });
    });

    trail.forEach((ping, idx) => {
      const ts = ping.timestamp ? new Date(ping.timestamp) : null;
      list.push({ type: 'ping', ping, idx, ts });
    });

    tasks.forEach((entry) => {
      const ts = entry.createdAt ? new Date(entry.createdAt) : null;
      list.push({ type: 'task', entry, ts });
    });

    return list.sort((a, b) => {
      if (!a.ts && !b.ts) return 0;
      if (!a.ts) return 1;
      if (!b.ts) return -1;
      return a.ts - b.ts;
    });
  }, [row, trail, tasks]);

  const isEmpty   = items.length === 0;
  const isLoading = trailLoading || tasksLoading;

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, pb: 2 }}>
      {isLoading && items.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1.5 }}>
          <CircularProgress size={18} sx={{ color: '#1075BA' }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#4a6a85' }}>Loading…</Typography>
        </Box>
      ) : isEmpty ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Timeline sx={{ fontSize: 36, color: '#c8d4dc', mb: 1 }} />
          <Typography sx={{ fontSize: '0.8rem', color: '#6b7f8e' }}>No activity yet</Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative' }}>
          {/* Vertical connector line */}
          <Box sx={{
            position: 'absolute', left: 11, top: 12, bottom: 12, width: 2,
            background: 'linear-gradient(to bottom, #0a9e74, #1075BA 50%, #c06000)',
            opacity: 0.18, borderRadius: 1, zIndex: 0,
          }} />

          <Stack spacing={0} sx={{ position: 'relative', zIndex: 1 }}>
            {items.map((item, i) => {
              const isLast = i === items.length - 1;
              if (item.type === 'event') return <EventItem key={item.key}  item={item} isLast={isLast} />;
              if (item.type === 'ping')  return <PingItem  key={item.ping._id ?? `ping-${i}`} item={item} isLast={isLast} />;
              if (item.type === 'task')  return <TaskItem  key={item.entry._id} item={item} isLast={isLast} onOpenEntry={onOpenEntry} />;
              return null;
            })}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// ── Individual timeline item components ───────────────────────────────────────

function EventItem({ item, isLast }) {
  const { label, color, data, isBreakBoundary } = item;
  const timeStr = item.ts ? format(item.ts, 'hh:mm a') : '—';
  const selfie  = data.imageSelfie?.data;
  const store   = data.imageStore?.data;
  const lat     = parseFloat(data.lat);
  const lng     = parseFloat(data.long ?? data.lng);
  const hasCoords = !isNaN(lat) && !isNaN(lng);

  return (
    <Box sx={{ display: 'flex', gap: 1.5, pb: isLast ? 0 : 1.25 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 24 }}>
        <Box sx={{
          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
          background: color, border: '2.5px solid #fff',
          boxShadow: `0 0 0 2px ${color}40, 0 2px 6px rgba(15,33,55,0.2)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isBreakBoundary
            ? <Box sx={{ width: 8, height: 2, background: '#fff', borderRadius: 1 }} />
            : <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />
          }
        </Box>
      </Box>

      <Box sx={{
        flex: 1, minWidth: 0,
        borderRadius: '10px',
        border: `1px solid ${alpha(color, 0.2)}`,
        background: alpha(color, 0.05),
        overflow: 'hidden',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.85 }}>
          <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color, flex: 1 }}>{label}</Typography>
          <Typography sx={{ fontSize: '0.68rem', color: '#4a6a85', fontFamily: 'monospace', fontWeight: 600 }}>{timeStr}</Typography>
        </Box>

        {(selfie || store || hasCoords) && (
          <Box sx={{ px: 1.25, pb: 0.9, display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
            {selfie && (
              <Box sx={{ width: 34, height: 34, borderRadius: '6px', overflow: 'hidden', border: `1.5px solid ${color}`, flexShrink: 0 }}>
                <PhotoCard src={selfie} label="Selfie" icon={<Person sx={{ fontSize: 13 }} />} />
              </Box>
            )}
            {store && (
              <Box sx={{ width: 34, height: 34, borderRadius: '6px', overflow: 'hidden', border: `1.5px solid ${alpha(color, 0.5)}`, flexShrink: 0 }}>
                <PhotoCard src={store} label="Environment" icon={<Person sx={{ fontSize: 13 }} />} />
              </Box>
            )}
            {hasCoords && (
              <Typography sx={{ fontSize: '0.62rem', color: '#6b7f8e', fontFamily: 'monospace' }}>
                {lat.toFixed(5)}, {lng.toFixed(5)}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function PingItem({ item, isLast }) {
  const { ping } = item;
  const netCfg  = NET_QUALITY_CONFIG[ping.deviceContext?.networkQuality ?? 'unknown'];
  const timeStr = item.ts ? format(item.ts, 'hh:mm a') : '—';

  return (
    <Box sx={{ display: 'flex', gap: 1.5, pb: isLast ? 0 : 0.75 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 24, pt: 0.5 }}>
        <Box sx={{
          width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
          background: '#1075BA', border: '2px solid #fff',
          boxShadow: '0 1px 4px rgba(15,33,55,0.2)',
        }} />
      </Box>

      <Box sx={{
        flex: 1, minWidth: 0,
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
        py: 0.5,
        borderBottom: isLast ? 'none' : '1px solid rgba(16,117,186,0.06)',
      }}>
        <LocationOn sx={{ fontSize: 12, color: '#1075BA', flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.68rem', color: '#4a6a85', fontFamily: 'monospace', fontWeight: 600 }}>{timeStr}</Typography>
        {ping.location?.latitude != null && (
          <Typography sx={{ fontSize: '0.63rem', color: '#9aa8b4', fontFamily: 'monospace' }}>
            {ping.location.latitude.toFixed(4)}, {ping.location.longitude.toFixed(4)}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, ml: 'auto' }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: netCfg.color }} />
          <Typography sx={{ fontSize: '0.62rem', color: netCfg.color, fontWeight: 600 }}>{netCfg.label}</Typography>
        </Box>
        {ping.isLate && (
          <Box sx={{ fontSize: '0.58rem', color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca', px: 0.5, borderRadius: '3px', fontWeight: 700 }}>
            Late
          </Box>
        )}
      </Box>
    </Box>
  );
}

function TaskItem({ item, isLast, onOpenEntry }) {
  const { entry } = item;
  const isLive    = !!entry._isLive;
  const timeStr   = item.ts ? format(item.ts, 'hh:mm a') : '—';
  const imgs      = extractImages(entry.formData);

  const flat       = flatFormData(entry.formData);
  const textFields = Object.entries(flat).filter(([k, v]) => {
    if (k === 'location' || k.startsWith('location.')) return false;
    if (Array.isArray(v) && v[0]?.url) return false;
    if (v && typeof v === 'object' && v.url) return false;
    return true;
  });

  const accentColor = isLive ? '#0a9e74' : '#1075BA';

  return (
    <Box sx={{ display: 'flex', gap: 1.5, pb: isLast ? 0 : 1.25 }}>
      {/* Diamond icon */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 24 }}>
        <Box sx={{
          width: 20, height: 20, flexShrink: 0,
          background: isLive
            ? 'linear-gradient(135deg, #0a9e74, #1a8fd6)'
            : 'linear-gradient(135deg, #1075BA, #1a8fd6)',
          border: '2px solid #fff', borderRadius: '5px',
          transform: 'rotate(45deg)',
          boxShadow: isLive
            ? '0 2px 6px rgba(10,158,116,0.45), 0 0 0 3px rgba(10,158,116,0.2)'
            : '0 2px 6px rgba(16,117,186,0.3)',
          mt: 0.25,
          animation: isLive ? 'pulseNew 1.2s ease-out 1' : 'none',
        }} />
      </Box>

      <Box sx={{
        flex: 1, minWidth: 0,
        borderRadius: '10px',
        border: isLive ? '1px solid rgba(10,158,116,0.3)' : '1px solid rgba(16,117,186,0.14)',
        background: isLive ? alpha('#0a9e74', 0.04) : alpha('#1075BA', 0.03),
        overflow: 'hidden',
      }}>
        {/* Task header row */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.25, pt: 1, pb: 0.75,
          borderBottom: (textFields.length > 0 || imgs.length > 0)
            ? `1px solid ${isLive ? 'rgba(10,158,116,0.1)' : 'rgba(16,117,186,0.08)'}` : 'none',
        }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: '7px', flexShrink: 0,
            background: isLive
              ? 'linear-gradient(135deg, #0a9e74, #1a8fd6)'
              : 'linear-gradient(135deg, #1075BA, #1a8fd6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Assignment sx={{ color: '#fff', fontSize: 15 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#0f2137', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {entry.taskLabel || entry.taskId}
              </Typography>
              {isLive && (
                <Box sx={{ fontSize: '0.55rem', fontWeight: 800, color: '#0a9e74', background: '#e6f9f4', border: '1px solid #b3ead9', px: 0.6, py: 0.1, borderRadius: '4px', flexShrink: 0, letterSpacing: '0.04em' }}>
                  LIVE
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.62rem', color: '#6b7f8e', fontFamily: 'monospace' }}>{timeStr}</Typography>
              <Box sx={{
                fontSize: '0.58rem', fontWeight: 700,
                color: entry.synced ? '#0a9e74' : '#c06000',
                background: entry.synced ? '#e6f9f4' : '#fff4e0',
                border: `1px solid ${entry.synced ? '#b3ead9' : '#ffd599'}`,
                px: 0.5, borderRadius: '3px',
              }}>
                {entry.synced ? 'Synced' : 'Offline'}
              </Box>
            </Box>
          </Box>

          {/* Open detail button */}
          <Box
            onClick={() => onOpenEntry(entry)}
            sx={{ color: '#9aa8b4', cursor: 'pointer', p: 0.5, borderRadius: '6px', '&:hover': { color: '#1075BA', background: alpha('#1075BA', 0.08) } }}
          >
            <OpenInNew sx={{ fontSize: 13, display: 'block' }} />
          </Box>
        </Box>

        {/* Text field chips */}
        {textFields.length > 0 && (
          <Box sx={{ px: 1.25, pt: 0.85, pb: imgs.length > 0 ? 0 : 0.85, display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
            {textFields.map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, background: '#f0f4f8', borderRadius: '6px', px: 0.75, py: 0.35, border: '1px solid rgba(16,117,186,0.1)', maxWidth: '100%' }}>
                <Typography sx={{ fontSize: '0.6rem', color: '#6b7f8e', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>
                  {prettyKey(k)}
                </Typography>
                <Typography sx={{ fontSize: '0.62rem', color: '#0f2137', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>
                  {String(v)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Photo thumbnails */}
        {imgs.length > 0 && (
          <Box sx={{ px: 1.25, pt: 0.75, pb: 1, display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
            {imgs.map((img, ii) => (
              <Box
                key={ii} component="a" href={img.url} target="_blank" rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  width: 46, height: 46, borderRadius: '7px', overflow: 'hidden',
                  border: '1.5px solid rgba(16,117,186,0.2)', flexShrink: 0,
                  cursor: 'zoom-in', '&:hover': { opacity: 0.82, border: '1.5px solid #1075BA' },
                }}
              >
                <Box component="img" src={img.url} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
